// DeskPanel — an open binder (a module) or shelf (a course), lifted over its
// spot on the Studio desk (pen "Studio – the desk", 2 + 3): the head (kind,
// count, Close), the title, the breadcrumb, the rows in reading order, and
// the readiness line before export with "Generate it" (DeskBriefs.tsx), and
// the foot (where it lives, Export). The rows end in "Plan a document" (a
// binder) or "Plan a module" (a shelf). A binder's rows are its documents
// (DeskRows.tsx); a shelf's are its modules, each with a spine, unfolding in
// place or opening on top. Rows reorder by dragging their grip.
//
// Pure: what is in focus and where is projected in; Close, the breadcrumb,
// Esc, opening a row, a row move and Export go out as intents. Local state
// is view-ephemeral only (which shelf rows are unfolded, a row drag).

export { DeskPanel };

import { plural } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { type CSSProperties, type ReactNode, useEffect, useRef } from "react";
import type { ExportReadiness } from "../../app/ports/brief.js";
import { Icon } from "../Icon/Icon.js";
import { Kbd } from "../Kbd/Kbd.js";
import type { PartIntent } from "../ModuleCard/partOps.js";
import type { ContainerCard } from "../Studio/studioCards.js";
import type { DeskEntry } from "../Studio/studioDesk.js";
import type { Point } from "../Studio/studioLayout.js";
import { PlanRow, ReadinessLine } from "./DeskBriefs.js";
import { type DeskCrumb, DeskCrumbs } from "./DeskCrumbs.js";
import { BinderRows, ShelfRows } from "./DeskRows.js";
import { type DeskNote, documentCount, toGenerate } from "./deskFacts.js";
import { deskKeyDown } from "./deskKeys.js";

type DeskPanelIntents = {
    // Close this one (one level up).
    readonly onClose: () => void;
    // The breadcrumb: back to the level of `id` (null: the desk).
    readonly onCrumb: (id: string | null) => void;
    // A row: open it (one level in), or move it.
    readonly onPart: (intent: PartIntent) => void;
    readonly onOpenModule: (id: string) => void;
    readonly onExport: () => void;
    // Briefs: plan a document (binder) or module (shelf), generate the
    // planned documents.
    readonly onPlanDocument: (title: string, brief: string) => void;
    readonly onPlanModule: (title: string) => void;
    readonly onGenerateAll: (ids: readonly string[]) => void;
};

const DeskPanel = ({
    entry,
    card,
    project,
    crumbs,
    at,
    liftFrom,
    online,
    readiness,
    note,
    intents,
}: {
    readonly entry: DeskEntry;
    readonly card: ContainerCard;
    // The project's name, and the levels above it, from the outermost down
    // (not itself).
    readonly project: string;
    readonly crumbs: readonly DeskCrumb[];
    // Where it lies on screen (viewport pixels).
    readonly at: Point;
    // The card's width over the panel's: the open grows from the card.
    readonly liftFrom: number | undefined;
    readonly online: boolean;
    // Its export readiness (absent: not known yet), and what came of
    // "Generate it".
    readonly readiness: ExportReadiness | undefined;
    readonly note: DeskNote | undefined;
    readonly intents: DeskPanelIntents;
}): ReactNode => {
    const { t } = useLingui();
    const self = useRef<HTMLElement>(null);
    // Opened: keyboard focus moves into it, so Esc closes it.
    useEffect(() => {
        self.current?.focus({ preventScroll: true });
    }, []);
    const shelf = entry.kind === "shelf";
    const documents = documentCount(card);
    const modules = card.parts.filter((p) => p.kind === "module").length;
    const style: CSSProperties & Record<"--lift-from", string> = {
        left: at.x,
        top: at.y,
        "--lift-from": liftFrom === undefined ? "0.96" : String(liftFrom),
    };
    return (
        <section
            ref={self}
            role="dialog"
            aria-label={card.title}
            tabIndex={-1}
            className="bilda-desk__panel"
            data-kind={entry.kind}
            data-desk-id={entry.id}
            style={style}
            onKeyDown={deskKeyDown(intents.onClose)}
        >
            <header className="bilda-desk__head">
                <span className="bilda-desk__kind">
                    <Icon name={shelf ? "course" : "module"} size="sm" />
                    {shelf ? t`Course` : t`Module`}
                </span>
                <span className="bilda-desk__count">
                    {shelf
                        ? t`${plural(modules, { one: "# module", other: "# modules" })} · ${plural(documents, { one: "# document", other: "# documents" })}`
                        : t`${plural(documents, { one: "# document", other: "# documents" })}`}
                </span>
                <button
                    type="button"
                    className="bilda-desk__close"
                    onClick={intents.onClose}
                >
                    {t`Close`}
                    <Kbd
                        className="bilda-desk__esc"
                        keys={["Esc"]}
                        aria-hidden="true"
                    />
                </button>
            </header>
            <h2 className="bilda-desk__title" title={card.title}>
                {card.title}
            </h2>
            <DeskCrumbs
                project={project}
                crumbs={crumbs}
                current={card.title}
                onCrumb={intents.onCrumb}
            />
            {shelf ? (
                <ShelfRows
                    card={card}
                    online={online}
                    onPart={intents.onPart}
                    onOpenModule={intents.onOpenModule}
                />
            ) : (
                <BinderRows
                    card={card}
                    online={online}
                    onPart={intents.onPart}
                />
            )}
            <PlanRow
                what={shelf ? "module" : "document"}
                online={online}
                onPlan={(title, brief) =>
                    shelf
                        ? intents.onPlanModule(title)
                        : intents.onPlanDocument(title, brief)
                }
            />
            <ReadinessLine
                readiness={readiness}
                planned={
                    readiness === undefined ? [] : toGenerate(readiness, card)
                }
                note={note}
                online={online}
                onGenerateAll={intents.onGenerateAll}
            />
            <footer className="bilda-desk__foot">
                <span className="bilda-desk__home">
                    <Icon name="library" size="sm" />
                    {t`In Library · Draft`}
                </span>
                <button
                    type="button"
                    className="bilda-desk__export"
                    disabled={!online}
                    onClick={intents.onExport}
                >
                    {t`Export …`}
                </button>
            </footer>
        </section>
    );
};
